import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Edit, Key, Trash2, Shield, Users, UserCog, Check, Minus } from 'lucide-react';
import UserForm, { User } from '../components/UserForm';
import ResetPasswordForm from '../components/ResetPasswordForm';
import DataTable from '../components/DataTable';
import IconButton from '../components/common/IconButton';
import SmartSearchBar from '../components/leads/SmartSearchBar';
import { usersApi, UserDto, Role, roleLabel } from '../services/usersService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--surface-light, #f3f4f6);
  padding: 4px 12px;
  border-radius: 16px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-light);
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;
  flex-wrap: wrap;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  ${props => props.$isActive ? `
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  ` : `
    background: white;
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover {
      border-color: var(--primary-color);
      color: var(--primary-color);
    }
  `}
`;

const StatusSelect = styled.select`
  padding: 10px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const PermissionText = styled.span<{ level: number }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  filter: saturate(0.7) brightness(0.85);

  ${props => {
    switch (props.level) {
      case 1:
        return `color: #dc2626;`;
      case 2:
        return `color: #d97706;`;
      case 3:
        return `color: #2563eb;`;
      case 4:
        return `color: #7c3aed;`;
      default:
        return `color: #6b7280;`;
    }
  }}
`;

const StatusText = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$active ? '#16a34a' : '#dc2626'};
  filter: saturate(0.7) brightness(0.85);
`;

const FlagList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`;

const FlagItem = styled.span<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${props => props.$on ? 'var(--text-primary)' : 'var(--text-secondary)'};
`;

const DeptChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  justify-content: center;
`;

const DeptChip = styled.span`
  font-size: 11px;
  color: var(--text-primary);
  background: #e6f0ff;
  border: 1px solid #c2dbff;
  padding: 1px 6px;
  border-radius: 999px;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const CenteredCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const TextCell = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const SecondaryText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid var(--error-color);
`;

const SuccessMessage = styled.div`
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid rgba(22, 163, 74, 0.3);
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmModal = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  padding: 24px;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ConfirmTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const ConfirmText = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: var(--text-secondary);
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

interface UserResponse extends UserDto {
  lastLoginAt: string | null;
}

const UsersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [isResetPasswordFormOpen, setIsResetPasswordFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Filters - read from URL
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [roleFilter, setRoleFilter] = useState<string[]>(() => {
    const roles = searchParams.get('roles');
    return roles ? roles.split(',') : [];
  });
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('status') || '');

  // Delete confirmation
  const [userToDelete, setUserToDelete] = useState<UserResponse | null>(null);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (searchTerm) params.set('search', searchTerm);
    if (roleFilter.length > 0) params.set('roles', roleFilter.join(','));
    if (statusFilter) params.set('status', statusFilter);

    setSearchParams(params, { replace: true });
  }, [searchTerm, roleFilter, statusFilter, setSearchParams]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await usersApi.getAll();
      setUsers(data as UserResponse[]);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ type: 'error', text: 'שגיאה בטעינת המשתמשים' });
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          user.fullName.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search) ||
          user.phone.includes(search);
        if (!matchesSearch) return false;
      }

      // Role filter (multi-select)
      if (roleFilter.length > 0 && !roleFilter.includes(user.role.toString())) {
        return false;
      }

      if (statusFilter === 'active' && !user.isActive) return false;
      if (statusFilter === 'inactive' && user.isActive) return false;

      return true;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Toggle role filter (multi-select)
  const handleRoleToggle = (role: string) => {
    if (role === '') {
      setRoleFilter([]);
    } else if (roleFilter.includes(role)) {
      setRoleFilter(roleFilter.filter(r => r !== role));
    } else {
      setRoleFilter([...roleFilter, role]);
    }
  };

  const extractError = (error: any, fallback: string): string => {
    if (error.response?.data?.errors) {
      const messages: string[] = [];
      Object.values(error.response.data.errors).forEach((value: any) => {
        if (Array.isArray(value)) messages.push(...value);
        else messages.push(String(value));
      });
      return messages.join(', ');
    }
    if (error.response?.data?.message) return error.response.data.message;
    if (typeof error.response?.data === 'string') return error.response.data;
    return fallback;
  };

  const handleCreateUser = async (user: User, password?: string) => {
    try {
      setIsSubmitting(true);
      setMessage(null);

      await usersApi.create({
        fullName: user.fullName,
        email: user.email,
        phone: user.phone,
        role: user.role,
        canViewClientFinance: user.canViewClientFinance,
        canViewFinancialReports: user.canViewFinancialReports,
        departmentIds: user.departmentIds,
        password: password || '',
      });

      setMessage({ type: 'success', text: 'המשתמש נוסף בהצלחה' });
      await fetchUsers();
      setIsUserFormOpen(false);
    } catch (error: any) {
      console.error('Error creating user:', error);
      setMessage({ type: 'error', text: extractError(error, 'שגיאה ביצירת המשתמש') });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (user: User) => {
    if (!selectedUser) return;

    try {
      setIsSubmitting(true);
      setMessage(null);

      await usersApi.update(selectedUser.id, {
        fullName: user.fullName,
        email: user.email,
        phone: user.phone,
        role: user.role,
        isActive: user.isActive,
        canViewClientFinance: user.canViewClientFinance,
        canViewFinancialReports: user.canViewFinancialReports,
        departmentIds: user.departmentIds,
      });

      await fetchUsers();
      setIsUserFormOpen(false);
      setMessage({ type: 'success', text: 'המשתמש עודכן בהצלחה' });
    } catch (error: any) {
      console.error('Error updating user:', error);
      setMessage({ type: 'error', text: extractError(error, 'שגיאה בעדכון המשתמש') });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (newPassword: string) => {
    if (!selectedUser) return;

    try {
      setIsSubmitting(true);
      await usersApi.resetPassword(selectedUser.id, newPassword);
      setMessage({ type: 'success', text: 'הסיסמה עודכנה בהצלחה' });
      setIsResetPasswordFormOpen(false);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      setMessage({ type: 'error', text: extractError(error, 'שגיאה באיפוס הסיסמה') });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const result = await usersApi.remove(userToDelete.id);
      const successText = result?.message
        || (result?.wasDeactivated ? 'המשתמש הושבת' : 'המשתמש נמחק בהצלחה');
      setMessage({ type: 'success', text: successText });
      await fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setMessage({ type: 'error', text: extractError(error, 'שגיאה במחיקת המשתמש') });
    } finally {
      setUserToDelete(null);
    }
  };

  const openEditForm = (user: UserResponse) => {
    setSelectedUser(user);
    setIsUserFormOpen(true);
  };

  const openResetPasswordForm = (user: UserResponse) => {
    setSelectedUser(user);
    setIsResetPasswordFormOpen(true);
  };

  const closeForm = () => {
    setSelectedUser(null);
    setIsUserFormOpen(false);
    setIsResetPasswordFormOpen(false);
  };

  const getRoleIcon = (role: Role) => {
    switch (role) {
      case Role.Manager: return Shield;
      case Role.Agent: return UserCog;
      default: return Users;
    }
  };

  // מיפוי Role לצבע העמודה (משתמש בסכמת הצבעים הקיימת של PermissionText)
  const roleColorLevel = (role: Role): number => {
    switch (role) {
      case Role.Manager: return 1; // אדום
      case Role.Employee: return 3; // כחול
      default: return 2; // כתום (Agent)
    }
  };

  const formatLastLogin = (dateString: string | null): string => {
    if (!dateString) return 'טרם התחבר';
    const date = new Date(dateString);
    return date.toLocaleString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const columns = [
    {
      key: 'fullName',
      title: 'שם',
      width: '150px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <TextCell>{record.fullName}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'email',
      title: 'מייל',
      width: '200px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <TextCell>{record.email}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'phone',
      title: 'טלפון',
      width: '120px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <TextCell>{record.phone}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'role',
      title: 'תפקיד',
      width: '120px',
      render: (_: any, record: UserResponse) => {
        const Icon = getRoleIcon(record.role);
        return (
          <CenteredCell>
            <PermissionText level={roleColorLevel(record.role)}>
              <Icon size={14} />
              {record.roleName || roleLabel(record.role)}
            </PermissionText>
          </CenteredCell>
        );
      }
    },
    {
      key: 'finance',
      title: 'פיננסים',
      width: '150px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <FlagList>
            <FlagItem $on={record.canViewClientFinance}>
              {record.canViewClientFinance ? <Check size={13} color="#16a34a" strokeWidth={2.5} /> : <Minus size={13} color="#d1d5db" />}
              פיננסי לקוח
            </FlagItem>
            <FlagItem $on={record.canViewFinancialReports}>
              {record.canViewFinancialReports ? <Check size={13} color="#16a34a" strokeWidth={2.5} /> : <Minus size={13} color="#d1d5db" />}
              דוחות
            </FlagItem>
          </FlagList>
        </CenteredCell>
      )
    },
    {
      key: 'departments',
      title: 'מחלקות',
      width: '160px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          {record.departmentNames && record.departmentNames.length > 0 ? (
            <DeptChips>
              {record.departmentNames.map((name, i) => (
                <DeptChip key={i}>{name}</DeptChip>
              ))}
            </DeptChips>
          ) : (
            <SecondaryText>—</SecondaryText>
          )}
        </CenteredCell>
      )
    },
    {
      key: 'lastLoginAt',
      title: 'התחברות אחרונה',
      width: '160px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <SecondaryText>{formatLastLogin(record.lastLoginAt)}</SecondaryText>
        </CenteredCell>
      )
    },
    {
      key: 'isActive',
      title: 'סטטוס',
      width: '100px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <StatusText $active={record.isActive}>
            {record.isActive ? 'פעיל' : 'לא פעיל'}
          </StatusText>
        </CenteredCell>
      )
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '170px',
      render: (_: any, record: UserResponse) => (
        <ActionsCell>
          <IconButton
            icon={Edit}
            onClick={(e) => { e.stopPropagation(); openEditForm(record); }}
            title="עריכת משתמש"
          />
          <IconButton
            icon={Key}
            onClick={(e) => { e.stopPropagation(); openResetPasswordForm(record); }}
            title="איפוס סיסמה"
            variant="primary"
          />
          <IconButton
            icon={Trash2}
            onClick={(e) => { e.stopPropagation(); setUserToDelete(record); }}
            title="מחיקת משתמש"
            variant="danger"
          />
        </ActionsCell>
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRight>
          <PageTitle>ניהול משתמשים</PageTitle>
          <UserCount>{users.length} משתמשים</UserCount>
        </HeaderRight>
        <HeaderActions>
          <Button variant="primary" onClick={() => setIsUserFormOpen(true)}>
            <Plus size={16} />
            משתמש חדש
          </Button>
        </HeaderActions>
      </PageHeader>

      {message && (
        message.type === 'success' ? (
          <SuccessMessage>{message.text}</SuccessMessage>
        ) : (
          <ErrorMessage>{message.text}</ErrorMessage>
        )
      )}

      <FilterContainer>
        <SmartSearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="חיפוש לפי שם, אימייל או טלפון..."
        />

        <FilterRow>
          <TabsContainer>
            <Tab
              $isActive={roleFilter.length === 0}
              onClick={() => handleRoleToggle('')}
            >
              הכל
            </Tab>
            <Tab
              $isActive={roleFilter.includes(Role.Manager.toString())}
              onClick={() => handleRoleToggle(Role.Manager.toString())}
            >
              <Shield size={14} />
              מנהלים
            </Tab>
            <Tab
              $isActive={roleFilter.includes(Role.Employee.toString())}
              onClick={() => handleRoleToggle(Role.Employee.toString())}
            >
              <Users size={14} />
              עובדים
            </Tab>
            <Tab
              $isActive={roleFilter.includes(Role.Agent.toString())}
              onClick={() => handleRoleToggle(Role.Agent.toString())}
            >
              <UserCog size={14} />
              סוכנים
            </Tab>
          </TabsContainer>

          <StatusSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">כל הסטטוסים</option>
            <option value="active">פעילים</option>
            <option value="inactive">לא פעילים</option>
          </StatusSelect>
        </FilterRow>
      </FilterContainer>

      <DataTable
        data={filteredUsers}
        columns={columns}
        loading={loading}
      />

      <UserForm
        user={selectedUser ? {
          id: selectedUser.id,
          fullName: selectedUser.fullName,
          email: selectedUser.email,
          phone: selectedUser.phone,
          role: selectedUser.role,
          canViewClientFinance: selectedUser.canViewClientFinance,
          canViewFinancialReports: selectedUser.canViewFinancialReports,
          departmentIds: selectedUser.departmentIds ?? [],
          isActive: selectedUser.isActive,
          isDirectoryLinked: selectedUser.isDirectoryLinked,
        } : undefined}
        isOpen={isUserFormOpen}
        onClose={closeForm}
        onSubmit={selectedUser ? handleUpdateUser : handleCreateUser}
        isLoading={isSubmitting}
      />

      <ResetPasswordForm
        isOpen={isResetPasswordFormOpen}
        onClose={closeForm}
        onSubmit={handleResetPassword}
        userFullName={selectedUser?.fullName}
        userEmail={selectedUser?.email}
        isLoading={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      {userToDelete && (
        <ConfirmOverlay onClick={() => setUserToDelete(null)}>
          <ConfirmModal onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>מחיקת משתמש</ConfirmTitle>
            <ConfirmText>
              האם אתה בטוח שברצונך למחוק את המשתמש "{userToDelete.fullName}"?
              <br />
              פעולה זו אינה ניתנת לביטול.
            </ConfirmText>
            <ConfirmButtons>
              <Button variant="danger" onClick={handleDeleteUser}>
                מחק משתמש
              </Button>
              <Button onClick={() => setUserToDelete(null)}>
                ביטול
              </Button>
            </ConfirmButtons>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
    </PageContainer>
  );
};

export default UsersPage;
